import { createContext, useContext, type ReactNode } from 'react';
import type { TiptapFileSrcResolver } from '../../DukeTiptapEditor/useTiptapFileSrc';
import { resolveRelativePath } from './sharedStyles';

export type RemoteImageSrc = { uri: string; credentials: 'include' };

/**
 * Resolver for image / pdf references inside `@react-pdf/renderer`. Returns the
 * object form `{ uri, credentials: 'include' }` for file-service URLs so
 * `@react-pdf/image`'s default `same-origin` fetcher includes the cross-
 * subdomain CDN cookie. Returns a plain absolutised string for same-origin
 * legacy URLs.
 */
export type PdfMediaResolver = (
  fileDrn: string | null | undefined,
  raw: string,
) => string | RemoteImageSrc;

export const identityPdfMediaResolver: PdfMediaResolver = (_fileDrn, raw) =>
  resolveRelativePath(raw);

/**
 * Compose a TipTap resolver and a files-service endpoint into a PDF-specific
 * resolver. Apps wire this at the PDF-generator call site, where context
 * (`useTiptapFileSrcResolverFromContext`) and `getTenantContext` are reachable.
 */
export function buildPdfMediaResolver(
  resolveFileSrc: TiptapFileSrcResolver,
  filesEndpoint: string | undefined,
): PdfMediaResolver {
  return (fileDrn, raw) => {
    const resolved = resolveFileSrc(fileDrn, raw);
    const absolute = resolveRelativePath(resolved);
    if (filesEndpoint != null && filesEndpoint !== '' && absolute.startsWith(filesEndpoint)) {
      return { uri: absolute, credentials: 'include' };
    }
    return absolute;
  };
}

const PdfMediaResolverContext = createContext<PdfMediaResolver>(identityPdfMediaResolver);

export function PdfMediaResolverProvider({
  resolver,
  children,
}: {
  resolver: PdfMediaResolver;
  children: ReactNode;
}) {
  return (
    <PdfMediaResolverContext.Provider value={resolver}>{children}</PdfMediaResolverContext.Provider>
  );
}

export function usePdfMediaResolver(): PdfMediaResolver {
  return useContext(PdfMediaResolverContext);
}
